/**
 * A Server-Sent Events parser for `fetch` bodies.
 *
 * The browser's `EventSource` cannot send an `Authorization` header or a POST
 * body, so streamed AI drafts are read from a `fetch` response instead and
 * decoded here. Text is pushed in as it arrives - in whatever pieces the
 * network happened to cut it - and each complete event is handed on.
 *
 * Follows the WHATWG event-stream rules that matter to this API: lines end in
 * LF, CRLF or a lone CR (a CR at the end of one piece and LF at the start of the
 * next is one line break), `:` lines are comments (keep-alive pings), `data`
 * lines of one event are joined with newlines, and a blank line dispatches.
 */

export interface SseEvent {
  event: string;
  data: string;
  id?: string;
}

export interface SseParser {
  push(text: string): void;
  /** The stream ended. An event without its closing blank line is dropped, as the spec says. */
  end(): void;
}

export function createSseParser(onEvent: (event: SseEvent) => void): SseParser {
  let buffer = '';
  let skipLineFeed = false;
  let eventName = '';
  let data: string[] = [];
  let id: string | undefined;

  const dispatch = () => {
    if (data.length > 0) {
      onEvent({ event: eventName || 'message', data: data.join('\n'), ...(id === undefined ? {} : { id }) });
    }
    eventName = '';
    data = [];
  };

  const line = (text: string) => {
    if (text === '') {
      dispatch();
      return;
    }
    if (text.startsWith(':')) return;
    const colon = text.indexOf(':');
    const field = colon === -1 ? text : text.slice(0, colon);
    let value = colon === -1 ? '' : text.slice(colon + 1);
    if (value.startsWith(' ')) value = value.slice(1);
    if (field === 'event') eventName = value;
    else if (field === 'data') data.push(value);
    else if (field === 'id') id = value;
  };

  return {
    push(text: string) {
      let chunk = text;
      if (skipLineFeed && chunk.startsWith('\n')) chunk = chunk.slice(1);
      skipLineFeed = false;
      buffer += chunk;
      let start = 0;
      for (let index = 0; index < buffer.length; index += 1) {
        const char = buffer[index];
        if (char !== '\n' && char !== '\r') continue;
        line(buffer.slice(start, index));
        if (char === '\r') {
          if (index + 1 === buffer.length) skipLineFeed = true;
          else if (buffer[index + 1] === '\n') index += 1;
        }
        start = index + 1;
      }
      buffer = buffer.slice(start);
    },
    end() {
      buffer = '';
      eventName = '';
      data = [];
    },
  };
}
